import { useEffect } from "react";
import { isUserProfileServiceConfigured } from "src/env.js";
import {
	useGetProfileQuery,
	usePatchProfileMutation,
} from "src/common/service/userProfile/userProfileApi.js";
import {
	type LocalPreference,
	preferenceStorageKey,
	readLocalPreference,
	writeLocalPreference,
} from "src/common/utils/localPreference.js";
import { reconcilePreference } from "src/common/utils/reconcilePreference.js";

/** Background-only reconciliation for one preference key; never touches the rendered UI. `isValid`/`isEqual` must be stable. No-op when unconfigured. */
export const useSyncProfilePreference = <T>(
	key: string,
	isValid: (value: unknown) => value is T,
	isEqual?: (a: T, b: T) => boolean,
) => {
	const { data: profile } = useGetProfileQuery(undefined, {
		skip: !isUserProfileServiceConfigured,
	});
	const [patchProfile] = usePatchProfileMutation();

	useEffect(() => {
		if (!profile) {
			return;
		}

		const entry = profile.preferences[key];
		const remote: LocalPreference<T> | undefined =
			entry && isValid(entry.value)
				? { value: entry.value, updatedAt: entry.updatedAt }
				: undefined;

		const storageKey = preferenceStorageKey(key);
		const local = readLocalPreference<T>(storageKey);
		const { value, writeLocal, writeRemote } = reconcilePreference(
			local,
			remote,
			isEqual,
		);

		if (writeLocal && value !== undefined) {
			// Preserve the remote's own timestamp — this device only received it, didn't author it.
			writeLocalPreference(storageKey, value, remote?.updatedAt);
		}
		if (writeRemote && value !== undefined) {
			void patchProfile({ preferences: { [key]: value } })
				.unwrap()
				.catch(() => console.warn("Failed to sync preference", key));
		}
	}, [profile, key, patchProfile, isValid, isEqual]);
};
